import { useSyncExternalStore } from 'react';
import { useI18n } from '../i18n/useI18n';
import { shortGap } from './ago';

/** How often a "3m ago" on screen catches up with itself. */
const TICK_MS = 30_000;

/**
 * The current instant, as a value a render may read.
 *
 * Through a store rather than by calling the clock while drawing: a render that
 * reads `Date.now()` produces a different result every time it runs, which is
 * the definition of impure and is what React's own rules refuse. The store also
 * buys the thing this component actually wants — a figure that ages on screen
 * instead of freezing at whatever it was when the table was drawn.
 */
function subscribe(onChange: () => void): () => void {
  const timer = setInterval(onChange, TICK_MS);
  return () => {
    clearInterval(timer);
  };
}

function useNow(): number {
  return useSyncExternalStore(subscribe, Date.now, () => 0);
}

/**
 * When something happened, as a person asks it: "4 days ago", with the instant
 * itself on hover.
 *
 * Which day a node joined or a row was written is rarely the question; how long
 * ago almost always is. The exact moment is never lost — it is the title — so
 * this trades nothing away, it only puts the useful half first.
 *
 * Muted, because a time is not an entity. The rule this console follows is that
 * a cell naming something else — a person, a node, a resource — carries the
 * text colour, and everything around it recedes.
 */
export function Ago({ at, className = 'muted' }: { at: string | Date; className?: string }) {
  const { t, formatDateTime } = useI18n();
  const now = useNow();
  const when = typeof at === 'string' ? new Date(at) : at;
  return (
    <span className={className} title={formatDateTime(when)}>
      {t('time.ago', { value: shortGap(when.getTime(), now) })}
    </span>
  );
}

/** The same, for a Unix second — which is how the cluster states its instants. */
export function AgoUnix({ at }: { at: number }) {
  return <Ago at={new Date(at * 1000)} />;
}
